import {
  AdminSpinner,
  AgentCustomersStatsRecoveryResponse,
  AgentCustomerStatsResponse,
  ArrowRed,
  Box,
  displayCentsInDollars,
  Div,
  getCustomerStatsRecovery,
  GrubconLogoWhiteSimbolSmall,
  H3,
  H5,
  Icon,
  IconButtonStyled,
  lightTheme,
  ModalPortal,
  P,
} from '@vgrubs/components';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

export const fetchCustomerStatsVGrubs = async ({
  pageParam = 1,
}: {
  pageParam: number;
}) => {
  const { data } = await getCustomerStatsRecovery({
    limit: 20,
    page: pageParam,
  });

  // If data length is less than the limit, we know there is no next page
  const nextPage = data.data.length === 20 ? pageParam + 1 : undefined;

  return {
    customerStatsRecovery: data ?? [],
    nextPage,
  };
};

const GrubConciergeStats = ({
  stats,
}: {
  stats: AgentCustomerStatsResponse;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recoveryLocations, setRecoveryLocations] = useState<
    AgentCustomersStatsRecoveryResponse[]
  >([]);

  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [ref, inView] = useInView();

  const getData = async () => {
    if (loading) return;
    setLoading(true);
    if (page !== 1) {
      setLoadingMore(true);
    }
    const data = await fetchCustomerStatsVGrubs({
      pageParam: page,
    });

    try {
      if (data.nextPage) {
        setHasMore(true);
        setPage(data.nextPage);
      } else {
        setHasMore(false);
      }
      if (data.customerStatsRecovery.data.length > 0) {
        setRecoveryLocations((prev) => [
          ...prev,
          ...data.customerStatsRecovery.data,
        ]);
      }
    } finally {
      setLoadingMore(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (inView && hasMore) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <>
      <Box $p="16px">
        <Div $flex $alignCenter $gap="8px" $between $p="0 0 16px 0">
          <Div $flex $alignCenter $gap="8px">
            <Icon $size={40} $sizeSM={40}>
              <GrubconLogoWhiteSimbolSmall />
            </Icon>
            <H3 $m="0">Grub Concierge</H3>
          </Div>
          <IconButtonStyled onClick={() => setIsOpen(true)}>
            <Icon $size={24} $sizeSM={24}>
              <ArrowRed />
            </Icon>
          </IconButtonStyled>
        </Div>
        <Div $flex $column>
          {stats?.recovery.map((stat, index) => (
            <Div
              $flex
              $alignCenter
              key={stat.locationId}
              $between
              $gap="8px"
              $minHeight="50px"
              style={{
                borderBottom: '1px solid #e3e3e3',
                borderTop: index !== 0 ? 'none' : '1px solid #e3e3e3',
              }}
            >
              <P $bold $mb="0">
                {stat.locationName}
              </P>{' '}
              <P $m="0" $right style={{ flex: 'none' }}>
                Recovered:{' '}
                <span style={{ color: lightTheme.green }}>
                  {displayCentsInDollars(stat.generatedAmount || 0)}
                </span>
              </P>
            </Div>
          ))}
        </Div>
        <Div $flex $alignCenter $justifyEnd $gap="8px" $mt="16" $mb="4">
          <H5 $bold $mb="0">
            Total Recovered:
          </H5>
          <H5 $m="0" $right style={{ flex: 'none' }}>
            <span style={{ color: lightTheme.green }}>
              {displayCentsInDollars(stats?.recoveryTotal || 0)}
            </span>
          </H5>
        </Div>
      </Box>
      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        $numOfFloatingButtons={1}
        logo={false}
        width="600px"
        maxHeight="calc(65px * 10)"
        modalOverflow="hidden"
        titleWithExit={'Grub Concierge'}
        footer={
          <Div $flex $alignEnd $justifyEnd $gap="8px" $mt="50">
            <H5 $bold $mb="0">
              Total Recovered:
            </H5>
            <H5 $m="0" $right style={{ flex: 'none' }}>
              <span style={{ color: lightTheme.green }}>
                {displayCentsInDollars(stats?.recoveryTotal || 0)}
              </span>
            </H5>
          </Div>
        }
      >
        {loadingMore && <AdminSpinner small absolute />}

        {recoveryLocations?.map((item, index) => (
          <Div
            $flex
            $alignCenter
            $gap="8px"
            key={item.locationId}
            $between
            $gapSM="8px"
            $minHeight="50px"
            style={{
              borderBottom: '1px solid #e3e3e3',
              borderTop: index !== 0 ? 'none' : '1px solid #e3e3e3',
            }}
          >
            <P $bold $mb="0">
              {index + 1}. {item.locationName}
            </P>{' '}
            <P $m="0" $right style={{ flex: 'none' }}>
              Recovered:{' '}
              <span style={{ color: lightTheme.green }}>
                {displayCentsInDollars(item.generatedAmount || 0)}
              </span>
            </P>
          </Div>
        ))}

        <Div $height="20px" ref={ref}></Div>
      </ModalPortal>
    </>
  );
};

export { GrubConciergeStats };
